import { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Nav from "@/components/landing/Nav";
import Gallery from "@/components/landing/Gallery";
import Footer from "@/components/landing/Footer";

const featuredPost = {
  date: "22 October 2026",
  category: "Tax Deadlines",
  title: "The 31 October Income Tax Deadline: A Business Owner's Checklist",
  intro: "Nine days from now, self-assessed income tax returns are due. Here is what to gather, what it costs to be late, and why filing early is the cheapest advice we give all year.",
  paragraphs: [
    "If you are self-employed, a company director with non-PAYE income, or a landlord, 31 October is your date with Revenue. It is the deadline to file your income tax return for last year and to pay any balance of tax due — and preliminary tax for the current year alongside it.",
    "Filing online through ROS? Revenue traditionally grants an extension to mid-November for those who both file and pay online. It is a welcome cushion — but treat it as exactly that, not a new deadline. Systems strain, banks delay, and the extension applies only if both the return and the payment go through.",
  ],
  pullQuote: "File within two months of the deadline and Revenue adds a 5% surcharge to your tax bill. Miss it by longer and that doubles to 10%. Late filing is the most expensive line item no business plans for.",
  closing: [
    "What to gather this week: up-to-date books, bank statements, sales and purchase invoices, receipts for expenses, and details of any income outside the business — rental, dividends, or a PAYE salary from elsewhere.",
    "And if the numbers are tight this year, do not go quiet. Revenue is consistently more flexible with those who engage before the deadline than with those who surface after it. A conversation now costs nothing; a surcharge later costs thousands.",
  ],
};

export default function Blog() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="blog-page">
      <Nav />
      <main>
        <section className="pt-36 lg:pt-44" data-testid="blog-header">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="overline-label mb-6 flex items-center gap-4"
              data-testid="blog-overline"
            >
              <span className="inline-block h-px w-10 bg-gold" />
              The Journal
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-2xl font-serif text-4xl font-medium leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl"
              data-testid="blog-heading"
            >
              Notes from the <em className="italic text-gold">practice.</em>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="mt-6 max-w-lg text-base leading-relaxed text-muted sm:text-lg"
            >
              Photographs and field notes from Kilkenny, plus practical guidance
              on the deadlines and decisions that matter to your business.
            </motion.p>
          </div>
        </section>
        <section className="py-16 lg:py-20" data-testid="blog-featured-article">
          <div className="mx-auto max-w-3xl px-6 lg:px-12">
            <motion.article
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="flex items-center gap-4 font-sans text-xs font-semibold uppercase tracking-[0.24em] text-navy/50" data-testid="article-meta">
                <span className="text-gold">{featuredPost.category}</span>
                <span className="inline-block h-px w-6 bg-gold/50" />
                <span>{featuredPost.date}</span>
              </div>

              <h2 className="mt-5 font-serif text-3xl font-medium leading-[1.1] tracking-tight text-navy sm:text-4xl lg:text-5xl" data-testid="article-title">
                {featuredPost.title}
              </h2>

              <p className="mt-6 font-serif text-xl italic leading-relaxed text-navy/70" data-testid="article-intro">
                {featuredPost.intro}
              </p>

              <div className="mt-8 space-y-6 text-base leading-relaxed text-muted sm:text-lg" data-testid="article-body">
                {featuredPost.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              <blockquote className="my-10 border-l-2 border-gold pl-6 font-serif text-xl italic leading-relaxed text-navy lg:text-2xl" data-testid="article-pullquote">
                {featuredPost.pullQuote}
              </blockquote>

              <div className="space-y-6 text-base leading-relaxed text-muted sm:text-lg">
                {featuredPost.closing.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              <button
                data-testid="article-cta-contact"
                onClick={() => navigate("/#contact-form")}
                className="group mt-12 flex items-center gap-3 border border-navy px-7 py-3.5 font-sans text-sm font-semibold uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-navy hover:text-cream"
              >
                Talk to Us Before the Deadline
                <ArrowRight size={16} className="text-gold transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </motion.article>
          </div>
        </section>

        <Gallery />
      </main>
      <Footer />
    </div>
  );
}
